import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Container, Paper, Box, Typography, CircularProgress, Alert, Chip, Button,
  Avatar, Divider, FormControl, InputLabel, Select, MenuItem, Grid, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PhoneIcon from '@mui/icons-material/Phone';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { apiService } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { normalizeImageUrl } from '../components/Post';

const STATUS_OPTIONS = [
  { value: 'unhandled', label: '未対応' },
  { value: 'in_progress', label: '対応中' },
  { value: 'confirmed', label: '確定' },
  { value: 'cancelled', label: 'キャンセル' },
];

const statusColor = (status) =>
  status === 'confirmed' ? 'success'
  : status === 'in_progress' ? 'info'
  : status === 'cancelled' ? 'error'
  : 'warning';

export default function ReservationDetail() {
  const { reservation_id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reservation, setReservation] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState('');

  // 企業用 全項目編集フォーム
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // 予約者用 キャンセル依頼
  const [cancelling, setCancelling] = useState(false);

  const role = currentUser?.role;
  // ステータス操作・編集は企業ユーザーのみ
  const canManage = role === 'corporate';
  // 予約者本人か（キャンセル依頼可能）
  const isReserver = reservation && currentUser && reservation.user_id === currentUser.id;

  const openEditDialog = () => {
    setEditForm({
      reservation_date: reservation.reservation_date || '',
      start_time: reservation.start_time || '',
      course_minutes: reservation.course_minutes || 60,
      hotel: reservation.hotel || '',
      memo: reservation.memo || '',
      reserver_name: reservation.reserver_name || '',
      reserver_phone: reservation.reserver_phone || '',
      status: reservation.status || 'unhandled',
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      const response = await apiService.put(`/api/users/reservations/${reservation_id}`, editForm);
      const data = response?.data || response || {};
      if (data.reservation) {
        setReservation(data.reservation);
        setNewStatus(data.reservation.status || 'unhandled');
        setEditOpen(false);
        setMessage('予約内容を更新しました');
      } else {
        alert(data.message || '更新に失敗しました');
      }
    } catch (err) {
      alert(err?.response?.data?.message || '更新に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelRequest = async () => {
    if (!window.confirm('この予約のキャンセル依頼を送信しますか？')) return;
    try {
      setCancelling(true);
      const response = await apiService.post(`/api/users/reservations/${reservation_id}/cancel-request`, {});
      const data = response?.data || response || {};
      setReservation((prev) => ({ ...prev, cancel_requested: true }));
      alert(data.message || 'キャンセル依頼を送信しました');
    } catch (err) {
      alert(err?.response?.data?.message || 'キャンセル依頼の送信に失敗しました');
    } finally {
      setCancelling(false);
    }
  };

  // 予約者へメッセージを送る（会話を作成/取得してメッセージ画面へ）
  const handleMessageReserver = async () => {
    if (!reservation?.user_id) return;
    try {
      await apiService.post('/api/messages/conversations', {
        participant_ids: [reservation.user_id],
        check_only: true,
      });
    } catch (e) {
      // 会話が無ければ後続の遷移先で作成されるため無視
    }
    // 予約者のユーザー名でメッセージ画面へ遷移
    if (reservation.reserver_username) {
      navigate(`/messages?page=${reservation.reserver_username}`);
    } else {
      navigate('/messages');
    }
  };

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservation_id]);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.get(`/api/users/reservations/${reservation_id}`);
      const data = response?.data || response || {};
      if (data.reservation) {
        setReservation(data.reservation);
        setNewStatus(data.reservation.status || 'unhandled');
      } else {
        setError(data.error || '仮予約が見つかりません');
      }
    } catch (err) {
      setError(err?.response?.data?.error || '仮予約の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    try {
      setUpdating(true);
      setMessage('');
      const response = await apiService.put(`/api/users/reservations/${reservation_id}/status`, {
        status: newStatus,
      });
      const data = response?.data || response || {};
      if (data.status) {
        setReservation((prev) => ({ ...prev, status: data.status, status_label: data.status_label }));
        setMessage('ステータスを更新しました');
      } else {
        setMessage(data.message || 'ステータス更新に失敗しました');
      }
    } catch (err) {
      setMessage(err?.response?.data?.message || 'ステータス更新に失敗しました');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="outlined" onClick={() => navigate('/notifications')}>通知に戻る</Button>
      </Container>
    );
  }

  if (!reservation) return null;

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Button variant="text" onClick={() => navigate(-1)}>← 戻る</Button>
        {/* ロール別の予約管理へのリンク */}
        {role === 'corporate' && (
          <Button variant="outlined" size="small" onClick={() => navigate('/corporate/reservations')}>
            予約管理へ
          </Button>
        )}
        {role === 'poster' && (
          <Button variant="outlined" size="small" onClick={() => navigate('/my-reservations')}>
            予約管理へ
          </Button>
        )}
        {role === 'viewer' && isReserver && reservation.reserver_username && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(`/profile/${reservation.reserver_username}?tab=reservations`)}
          >
            予約タブへ
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h6">仮予約の詳細</Typography>
          <Chip
            label={reservation.status_label || '未対応'}
            color={statusColor(reservation.status)}
            size="small"
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* 対象投稿ユーザー */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Avatar src={normalizeImageUrl(reservation.poster_avatar)} alt={reservation.poster_display_name} />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {reservation.poster_display_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              @{reservation.poster_username}
            </Typography>
          </Box>
        </Box>

        {/* 日時・コース */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <CalendarMonthIcon sx={{ mr: 1 }} /> {reservation.reservation_date}
          </Typography>
          <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
            <ScheduleIcon sx={{ mr: 1 }} />
            {reservation.start_time} ～ {reservation.end_time}（{reservation.course_minutes}分コース）
          </Typography>
          {reservation.hotel && (
            <Typography variant="body1" sx={{ mt: 0.5 }}>
              ホテル（場所）: {reservation.hotel}
            </Typography>
          )}
        </Box>

        {/* 予約者情報 */}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>予約者</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          {reservation.reserver_username ? (
            // 予約者のプロフィールページへのリンク（手動登録で username が無い場合はリンクにしない）
            <Box
              component={Link}
              to={`/profile/${reservation.reserver_username}`}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, textDecoration: 'none', color: 'inherit' }}
            >
              <Avatar
                src={normalizeImageUrl(reservation.reserver_avatar)}
                alt={reservation.reserver_name}
                sx={{ width: 36, height: 36, '&:hover': { boxShadow: '0 0 0 2px #1976d2' } }}
              />
              <Box>
                <Typography variant="body1" sx={{ '&:hover': { textDecoration: 'underline', color: '#1976d2' } }}>
                  {reservation.reserver_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">@{reservation.reserver_username}</Typography>
              </Box>
            </Box>
          ) : (
            <>
              <Avatar src={normalizeImageUrl(reservation.reserver_avatar)} alt={reservation.reserver_name} sx={{ width: 36, height: 36 }} />
              <Box>
                <Typography variant="body1">{reservation.reserver_name}</Typography>
              </Box>
            </>
          )}
        </Box>
        {/* 電話番号は企業ユーザーのみ表示（バックエンドが reserver_phone を返した場合） */}
        {reservation.reserver_phone !== undefined && (
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
            <PhoneIcon sx={{ mr: 1, fontSize: 18 }} />
            {reservation.reserver_phone || '（電話番号未登録）'}
          </Typography>
        )}
        {/* 予約者へメッセージ（企業ユーザーのみ） */}
        {role === 'corporate' && reservation.user_id && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<MailOutlineIcon />}
            onClick={handleMessageReserver}
            sx={{ mt: 1 }}
          >
            予約者にメッセージを送る
          </Button>
        )}

        {reservation.memo && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2">その他要望</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{reservation.memo}</Typography>
          </>
        )}

        {/* キャンセル依頼済み表示 */}
        {reservation.cancel_requested && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            この予約はキャンセル依頼が出されています。
          </Alert>
        )}

        {/* 予約者用: キャンセル依頼ボタン */}
        {isReserver && !reservation.cancel_requested && reservation.status !== 'cancelled' && (
          <>
            <Divider sx={{ my: 2 }} />
            <Button
              variant="outlined"
              color="error"
              fullWidth
              onClick={handleCancelRequest}
              disabled={cancelling}
            >
              {cancelling ? '送信中...' : 'キャンセル依頼を送る'}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              ※ キャンセルは企業の確認後に確定します。
            </Typography>
          </>
        )}

        {/* 企業用: 予約を編集 */}
        {canManage && (
          <>
            <Divider sx={{ my: 2 }} />
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={openEditDialog}
              sx={{ mb: 1 }}
            >
              予約内容を編集
            </Button>
          </>
        )}

        {/* ステータス操作（企業） */}
        {canManage && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>対応ステータスを変更</Typography>
            {message && <Alert severity="info" sx={{ mb: 1 }}>{message}</Alert>}
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={8}>
                <FormControl fullWidth size="small">
                  <InputLabel id="status-label">ステータス</InputLabel>
                  <Select
                    labelId="status-label"
                    label="ステータス"
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleUpdateStatus}
                  disabled={updating || newStatus === reservation.status}
                >
                  {updating ? '更新中...' : '更新'}
                </Button>
              </Grid>
            </Grid>
          </>
        )}
      </Paper>

      {/* 企業用 全項目編集ダイアログ */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>予約内容を編集</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth label="予約日" type="date"
                value={editForm.reservation_date || ''}
                onChange={(e) => setEditForm({ ...editForm, reservation_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth label="開始時間" type="time"
                value={editForm.start_time || ''}
                onChange={(e) => setEditForm({ ...editForm, start_time: e.target.value })}
                InputLabelProps={{ shrink: true }} inputProps={{ step: 600 }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel id="edit-course-label">コース</InputLabel>
                <Select
                  labelId="edit-course-label"
                  label="コース"
                  value={editForm.course_minutes || 60}
                  onChange={(e) => setEditForm({ ...editForm, course_minutes: Number(e.target.value) })}
                >
                  {Array.from({ length: 34 }, (_, i) => 30 + i * 10).map((min) => (
                    <MenuItem key={min} value={min}>{min}分</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="ホテル（場所）"
                value={editForm.hotel || ''}
                onChange={(e) => setEditForm({ ...editForm, hotel: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth label="予約者名"
                value={editForm.reserver_name || ''}
                onChange={(e) => setEditForm({ ...editForm, reserver_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth label="電話番号"
                value={editForm.reserver_phone || ''}
                onChange={(e) => setEditForm({ ...editForm, reserver_phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="その他要望" multiline rows={2}
                value={editForm.memo || ''}
                onChange={(e) => setEditForm({ ...editForm, memo: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="edit-status-label">ステータス</InputLabel>
                <Select
                  labelId="edit-status-label"
                  label="ステータス"
                  value={editForm.status || 'unhandled'}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={saving}>
            {saving ? '保存中...' : '保存して予約者に通知'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
